import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * Estimated-delivery timeline for the product page.
 *
 * Lead times are merchant settings (processing days, shipping days, cutoff
 * hour, business-days switch) forwarded as data attributes. Dates are computed
 * on the client so a page served from cache — or from a bot that renders once
 * and reuses the HTML — never shows a stale delivery window.
 *
 * Everything is evaluated on the shopper's own calendar: the cutoff hour and
 * the counted days are read off the visitor's clock. That keeps the timeline
 * immediately legible to every visitor ("your today"), at the cost of a ±1 day
 * skew versus the Hong Kong warehouse's real cutoff for visitors far from
 * UTC+8 — accepted as within the noise of an 8–13 business-day window (owner
 * decision 2026-10-27, choosing simplicity over warehouse-exact cutoffs).
 */
class DeliveryTracker extends Component {
  /** @type {Element | null} - Cached section the variant listener is attached to. */
  #variantUpdateTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variantUpdateTarget = this.closest('.shopify-section, dialog');
    this.#variantUpdateTarget?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantUpdateTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * A variant change can re-render this block through a section update. The
   * dates stay the same, but the morphed markup arrives with empty slots, so
   * the timeline is repainted in place rather than re-fetching anything.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = () => {
    this.render();
  };

  /**
   * Reads the merchant settings, skipping any malformed value so one bad
   * number cannot blank the whole timeline.
   *
   * @param {string | undefined} name - The dataset key.
   * @param {number} fallback - Value used when the attribute is missing or unusable.
   * @returns {number} A non-negative integer.
   */
  #readNumber(name, fallback) {
    const raw = this.dataset[name];
    const value = raw === undefined ? NaN : parseInt(raw, 10);
    return Number.isFinite(value) && value >= 0 ? value : fallback;
  }

  /**
   * The delivery windows, computed from the shopper's calendar date.
   *
   * The arithmetic runs on plain calendar dates at UTC midnight so the weekday
   * of every step is stable and the UTC formatting in `createFormatter` shows
   * exactly the days that were counted.
   *
   * @returns {{ placed: Date, dispatchMin: Date, dispatchMax: Date, deliveryMin: Date, deliveryMax: Date }}
   */
  #computeSchedule() {
    const businessDaysOnly = this.dataset.businessDays !== 'false';
    const cutoffHour = Math.min(this.#readNumber('cutoffHour', 16), 23);
    const nowParts = localNowParts();

    let processingMin = this.#readNumber('procMin', 2);
    let processingMax = this.#readNumber('procMax', 3);
    if (processingMax < processingMin) processingMax = processingMin;

    let shippingMin = this.#readNumber('shipMin', 8);
    let shippingMax = this.#readNumber('shipMax', 13);
    if (shippingMax < shippingMin) shippingMax = shippingMin;

    // Work on plain calendar dates at UTC midnight: the weekday of such a date
    // is stable, so no timezone can shift the answer mid-computation.
    let placed = new Date(Date.UTC(nowParts.year, nowParts.month, nowParts.day));

    if (businessDaysOnly) {
      if (!isBusinessDay(placed) || nowParts.hour >= cutoffHour) {
        placed = nextBusinessDay(placed);
      }
    } else if (nowParts.hour >= cutoffHour) {
      placed.setUTCDate(placed.getUTCDate() + 1);
    }

    const dispatchMin = addDays(placed, processingMin, businessDaysOnly);
    const dispatchMax = addDays(placed, processingMax, businessDaysOnly);
    const deliveryMin = addDays(dispatchMin, shippingMin, businessDaysOnly);
    const deliveryMax = addDays(dispatchMax, shippingMax, businessDaysOnly);

    return { placed, dispatchMin, dispatchMax, deliveryMin, deliveryMax };
  }

  render() {
    const schedule = this.#computeSchedule();
    const formatter = createFormatter(this.dataset.locale);

    const dispatchWindow = formatWindow(formatter, schedule.dispatchMin, schedule.dispatchMax);
    const deliveryWindow = formatWindow(formatter, schedule.deliveryMin, schedule.deliveryMax);

    this.#setSlot('placed', formatDate(formatter, schedule.placed));
    this.#setSlot('dispatched', dispatchWindow);
    this.#setSlot('delivered', deliveryWindow);
  }

  /**
   * @param {string} slot - The `data-slot` name.
   * @param {string} value - Text to write.
   */
  #setSlot(slot, value) {
    const targets = this.querySelectorAll(`[data-slot="${slot}"]`);
    for (const target of targets) {
      target.textContent = value;
      target.removeAttribute('data-empty');
    }
  }
}

/**
 * @param {Date} date - The date to test.
 * @returns {boolean} Whether the date is Monday–Friday.
 */
function isBusinessDay(date) {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
}

/**
 * @param {Date} date - The starting date.
 * @returns {Date} The next Monday–Friday date after it.
 */
function nextBusinessDay(date) {
  const next = new Date(date);
  do {
    next.setUTCDate(next.getUTCDate() + 1);
  } while (!isBusinessDay(next));
  return next;
}

/**
 * Adds working days (skipping weekends) or plain calendar days.
 *
 * @param {Date} date - The starting date.
 * @param {number} days - How many days to add.
 * @param {boolean} businessDaysOnly - Whether to skip weekends.
 * @returns {Date} The resulting date.
 */
function addDays(date, days, businessDaysOnly) {
  const result = new Date(date);
  if (!businessDaysOnly) {
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  let added = 0;
  while (added < days) {
    result.setUTCDate(result.getUTCDate() + 1);
    if (isBusinessDay(result)) added++;
  }
  return result;
}

/**
 * The current date and hour on the shopper's own clock.
 *
 * Deliberately the visitor's timezone, not the warehouse's: the timeline is a
 * shopping aid, and a date that matches the visitor's calendar is the least
 * confusing presentation. The known cost is a ±1 day skew versus the Hong Kong
 * warehouse's true cutoff for visitors far from UTC+8 — accepted as within the
 * noise of an 8–13 business-day window (owner decision 2026-10-27).
 *
 * @returns {{ year: number, month: number, day: number, hour: number }}
 */
function localNowParts() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth(),
    day: now.getDate(),
    hour: now.getHours(),
  };
}

/**
 * @param {string | undefined} locale - The storefront locale.
 * @returns {Intl.DateTimeFormat} A formatter producing uppercase-ready "03 NOV".
 */
function createFormatter(locale) {
  // Dates are held at UTC midnight, so they must be formatted in UTC too —
  // otherwise a negative-offset visitor would see the previous day.
  const options = { day: '2-digit', month: 'short', timeZone: 'UTC' };
  try {
    return new Intl.DateTimeFormat(locale || undefined, options);
  } catch {
    return new Intl.DateTimeFormat('en', options);
  }
}

/**
 * @param {Intl.DateTimeFormat} formatter - The active formatter.
 * @param {Date} date - The date to format.
 * @returns {string} An uppercase date such as "03 NOV".
 */
function formatDate(formatter, date) {
  return formatter.format(date).toUpperCase();
}

/**
 * @param {Intl.DateTimeFormat} formatter - The active formatter.
 * @param {Date} start - First day of the window.
 * @param {Date} end - Last day of the window.
 * @returns {string} A single date when both ends match, otherwise a range.
 */
function formatWindow(formatter, start, end) {
  const first = formatDate(formatter, start);
  if (start.getTime() === end.getTime()) return first;
  return `${first} – ${formatDate(formatter, end)}`;
}

if (!customElements.get('delivery-tracker')) {
  customElements.define('delivery-tracker', DeliveryTracker);
}
